/**
 * ListingPhoto — the cover image for a marketplace listing (web, TD-135).
 *
 * Replaces the brand-tinted glyph placeholder tiles across the buyer surfaces
 * (browse card, listing detail, watchlist row). When the listing HAS a cover
 * image (`hasPhoto`), it streams the raw bytes behind auth and renders them as a
 * cover-fitted `<img>`; while loading, on error, or when the listing has no
 * photo, it falls back to the original eco glyph tile so every tile keeps a
 * stable, theme-token look in both light and dark mode.
 *
 * AUTH: the photo endpoint streams bytes behind a bearer (a plain <img src>
 * would NOT send the token), so we mirror src/api/photos.ts: fetch with the
 * Authorization header → blob → object URL, and revoke the URL on unmount /
 * id-change to avoid leaking blobs (see fetchListingPhotoObjectUrl in
 * src/api/marketplace.ts).
 *
 * This is a co-located non-route component in routes/ (the router tree is
 * assembled by hand in router.ts, so files here are NOT auto-registered as
 * routes — same convention as routes/FseMarkWeb.tsx).
 */
import { Box, Icon } from '@fse/ui';
import { useEffect, useState } from 'react';

import { fetchListingPhotoObjectUrl } from '../api/marketplace';

export interface ListingPhotoProps {
  /** Listing id — used to fetch /marketplace/listings/:id/photo. */
  listingId: string;
  /** Whether the listing has a cover image. When false we never fetch. */
  hasPhoto: boolean;
  /**
   * Fixed tile height in px (browse card 120, detail tile 160). Omit to let the
   * tile stretch to its flex container's height (watchlist row) — matching the
   * original `alignItems: 'stretch'` placeholder.
   */
  height?: number;
  /** Glyph size for the fallback (eco icon). Mirrors the original tile glyphs. */
  glyphSize: number;
  /** Fixed tile width in px (watchlist thumbnail); otherwise the tile is full-width. */
  width?: number;
  /** Accessible label for the image / glyph (the product name). */
  alt?: string;
}

/**
 * Renders the listing cover image when available, else the eco glyph fallback.
 * Used inside the existing tile wrappers, so it owns ONLY the inner content +
 * dimensions; callers keep their own WatchStar overlay + Card chrome.
 */
export function ListingPhoto({
  listingId,
  hasPhoto,
  height,
  glyphSize,
  width,
  alt,
}: ListingPhotoProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // No photo → never fetch; render the glyph fallback.
    if (!hasPhoto) {
      setObjectUrl(null);
      setFailed(false);
      return;
    }

    let cancelled = false;
    let url: string | null = null;
    setFailed(false);
    setObjectUrl(null);

    fetchListingPhotoObjectUrl(listingId)
      .then((u) => {
        if (cancelled) {
          // Unmounted / id changed mid-flight — revoke the orphaned blob.
          URL.revokeObjectURL(u);
          return;
        }
        url = u;
        setObjectUrl(u);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [listingId, hasPhoto]);

  const tileSx = {
    // Fixed height when given (browse/detail); otherwise stretch to the flex
    // row height (watchlist), mirroring the original alignItems:'stretch' tile.
    ...(height != null ? { height } : { alignSelf: 'stretch' as const, height: '100%' }),
    ...(width != null ? { width, flex: '0 0 auto' as const } : {}),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'action.hover',
    color: 'text.disabled',
    overflow: 'hidden',
  };

  // Loaded image — fills the tile, cover-cropped. The wrapper keeps the tinted
  // background so any letterboxing (shouldn't happen with cover) is on-brand.
  if (hasPhoto && objectUrl && !failed) {
    return (
      <Box sx={tileSx}>
        <Box
          component="img"
          src={objectUrl}
          alt={alt ?? ''}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </Box>
    );
  }

  // Loading / error / no-photo → the original eco glyph placeholder.
  return (
    <Box sx={tileSx}>
      <Icon name="eco" size={glyphSize} fill color="inherit" aria-label={alt} />
    </Box>
  );
}
